/**
 * The Nura aperture — the product's one visual signature.
 *
 * A ring with a single gap. It is geometry, not decoration, and it means
 * something specific in this architecture: a claim is a **bounded opening**
 * through which value passes exactly once. The bank opens it, the receiver
 * passes through it, and it closes. `PHASE_01_PRODUCTION_ARCHITECTURE.md` §12
 * draws that as a state machine; this draws it as a mark.
 *
 * Four uses, and no others — a signature stops being one the moment it is
 * sprinkled:
 *
 * 1. the wordmark, where it is Nura's own mark;
 * 2. the step indicator, where an open ring is the step in progress and a
 *    closed ring is one that completed;
 * 3. the ready state, where the ring closes on the transfer the bank approved;
 * 4. the receiver claim experience, where the open ring turns while the claim is
 *    in flight and closes around a check when it lands.
 *
 * The fourth is the same meaning as the second and third rather than a new one —
 * the opening a claim passes through, on the other side of it — which is why it
 * was added here instead of a spinner being introduced. It is recorded rather
 * than assumed: a component whose documentation says "three uses" while the code
 * has four has stopped being a constraint.
 *
 * ## Why it survives white-labelling
 *
 * It is drawn in `currentColor` at a fixed geometry. A bank's brand recolours
 * it; nothing a bank can set changes its shape. So a partner surface still
 * reads as the partner's, while the *form* stays recognisably Nura — which is
 * the only kind of signature a white-label product can honestly have.
 *
 * ## Why it survives everything else
 *
 * One path, no fills, no gradients, no text. It renders identically at 12px in
 * a wordmark and at 56px in a status card, in both themes, and it compresses
 * into a keyboard-sized surface without redrawing.
 */

/** Geometry, on the same 96-unit integer grid the icon set uses. */
const BOX = 96;
const CENTER = 48;
const RADIUS = 34;

/** Circumference, rounded to an integer so no decimal literal appears here. */
const CIRCUMFERENCE = Math.round(2 * Math.PI * RADIUS);

/** Arc length of the opening, as a share of the circumference. */
const GAP = Math.round(CIRCUMFERENCE / 7);

/**
 * Where the opening sits.
 *
 * Deliberately low-right. An arc with a gap at the top reads as a loading
 * spinner — every spinner in software rests there — and a payment product
 * cannot afford a mark that looks like it is waiting for something.
 */
const OPENING_ROTATION = 26;

export type ApertureState = "open" | "closed";

export interface ApertureOptions {
  /** `open` leaves the gap; `closed` completes the ring. */
  readonly state?: ApertureState;
  /** Stroke weight on the 96-unit grid. */
  readonly weight?: number;
  /**
   * Animate the ring closing when it renders.
   *
   * Ignored under `prefers-reduced-motion`, and ignored anywhere the caller
   * does not explicitly ask for it. Motion here marks one event — a bank
   * approving a transfer — and nothing else.
   */
  readonly draw?: boolean;
}

/**
 * Aperture markup at `size` CSS pixels.
 *
 * Decorative in every current use: it always accompanies visible text, so it
 * carries `aria-hidden` and never has to be described.
 */
export function aperture(size: number, options: ApertureOptions = {}): string {
  const px = Math.round(size);
  const weight = options.weight ?? 9;
  const closed = options.state === "closed";

  // An open ring is one dash and one gap; a closed ring is a single unbroken
  // dash the length of the whole circumference.
  const dash = closed ? `${CIRCUMFERENCE} 0` : `${CIRCUMFERENCE - GAP} ${GAP}`;
  const drawClass = options.draw === true ? " nura-aperture--draw" : "";

  return [
    `<svg class="nura-aperture${drawClass}" width="${px}" height="${px}"`,
    ` viewBox="0 0 ${BOX} ${BOX}" fill="none" aria-hidden="true" focusable="false">`,
    `<circle cx="${CENTER}" cy="${CENTER}" r="${RADIUS}"`,
    ` stroke="currentColor" stroke-width="${weight}" stroke-linecap="round"`,
    ` stroke-dasharray="${dash}" transform="rotate(${OPENING_ROTATION} ${CENTER} ${CENTER})"`,
    ` style="--nura-aperture-length:${CIRCUMFERENCE}"/>`,
    "</svg>",
  ].join("");
}
